import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Menu, X, Phone } from "lucide-react";
import { BUSINESS } from "../data/business";
import { track } from "../analytics";

const NAV_LINKS = [
    { label: "Início", href: "/" },
    { label: "Serviços", href: "/servicos" },
    { label: "Tributação", href: "/tributacao" },
    { label: "Reforma", href: "/reforma" },
    { label: "Notícias", href: "/noticias" },
    { label: "Contato", href: "/contato" },
];

const linkClass = ({ isActive }) =>
    `text-sm font-semibold transition-colors duration-200 ${isActive ? "text-blue-900" : "text-slate-600 hover:text-blue-900"}`;

const linkTestId = (href) => href === "/" ? "nav-link-inicio" : `nav-link-${href.slice(1)}`;

export const Navbar = () => {
    const [open, setOpen] = useState(false);
    const [hidden, setHidden] = useState(false);

    useEffect(() => {
        let lastY = window.scrollY;
        const onScroll = () => {
            const y = window.scrollY;
            setHidden(y > lastY && y > 140);
            lastY = y;
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header data-testid="navbar" className={`sticky top-0 z-50 bg-white/70 backdrop-blur-2xl border-b border-slate-200/70 shadow-[0_1px_12px_rgba(15,23,42,0.04)] transition-transform duration-300 ${hidden && !open ? "-translate-y-full" : "translate-y-0"}`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16 lg:h-20">
                    <a href="/" data-testid="navbar-logo" className="flex items-center gap-3 group">
                        <img
                            src="/logo-wbs.png"
                            alt="WBS Welton Barros Serviços Contábeis"
                            className="h-11 lg:h-12 w-auto transition-transform duration-300 group-hover:-translate-y-0.5"
                        />
                        <span className="leading-tight hidden sm:block">
                            <span className="block text-lg font-bold tracking-tight text-slate-900">WBS</span>
                            <span className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Serviços Contábeis</span>
                        </span>
                    </a>

                    <nav className="hidden lg:flex items-center gap-6" data-testid="navbar-links">
                        {NAV_LINKS.map((link) => (
                            <NavLink
                                key={link.href}
                                to={link.href}
                                end={link.href === "/"}
                                data-testid={linkTestId(link.href)}
                                className={linkClass}
                            >
                                {link.label}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="hidden lg:flex items-center gap-3">
                        <a href={`tel:${BUSINESS.phoneRaw}`} data-testid="nav-call-button" onClick={() => track("interaction", { label: "call", path: "/navbar" })} className="flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-blue-900 transition-colors duration-200">
                            <Phone className="w-4 h-4" />
                            {BUSINESS.phoneDisplay}
                        </a>
                        <Button asChild className="rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white transition-transform duration-200 hover:-translate-y-0.5 shadow-sm hover:shadow-md">
                            <a href={`${BUSINESS.whatsapp}?text=${encodeURIComponent("Olá! Quero um orçamento de contabilidade para minha empresa.")}`} target="_blank" rel="noopener noreferrer" data-testid="nav-whatsapp-button" onClick={() => track("interaction", { label: "whatsapp", path: "/navbar" })}>
                                Chamar no WhatsApp
                            </a>
                        </Button>
                    </div>

                    <button
                        data-testid="nav-mobile-toggle"
                        className="lg:hidden p-2 text-slate-700"
                        onClick={() => setOpen(!open)}
                        aria-label="Abrir menu"
                    >
                        {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>

                {open && (
                    <div className="lg:hidden pb-6 pt-2 space-y-4 animate-fade-up" data-testid="nav-mobile-menu">
                        {NAV_LINKS.map((link) => (
                            <NavLink
                                key={link.href}
                                to={link.href}
                                end={link.href === "/"}
                                data-testid={`nav-mobile-${linkTestId(link.href)}`}
                                onClick={() => setOpen(false)}
                                className={({ isActive }) =>
                                    `block text-base font-semibold ${isActive ? "text-blue-900" : "text-slate-700 hover:text-blue-900"}`
                                }
                            >
                                {link.label}
                            </NavLink>
                        ))}
                        <Button asChild className="w-full rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white">
                            <a href={`${BUSINESS.whatsapp}?text=${encodeURIComponent("Olá! Quero um orçamento de contabilidade para minha empresa.")}`} target="_blank" rel="noopener noreferrer" data-testid="nav-mobile-whatsapp-button">
                                Chamar no WhatsApp
                            </a>
                        </Button>
                    </div>
                )}
            </div>
        </header>
    );
};
